import type { BrowserTask } from "@ondit/publishing";
import type { DistributionPost } from "~/core/api/distribution";

export function dayKey(date: Date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function calendarDays(anchor: Date, view: "day" | "week" | "month") {
	const start = new Date(anchor.getFullYear(), anchor.getMonth(), view === "month" ? 1 : anchor.getDate());
	if (view !== "day") start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
	const end = view === "month" ? new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0) : new Date(start);
	if (view !== "day") end.setDate(end.getDate() + (6 - ((end.getDay() + 6) % 7)));
	const days: Date[] = [];
	for (const date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) days.push(new Date(date));
	return days;
}
export function localDateTime(value: string) {
	const date = new Date(value);
	return `${dayKey(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}
export function calendarHour(date: Date) {
	const hour = new Date(date);
	hour.setMinutes(0, 0, 0);
	return hour;
}
/**
 * A row that has a day and nothing behind it that will act on that day.
 *
 * The distinction the calendar exists to make. `planned` is a proposal — the
 * weekly loop writes one for every page it published, and a person makes one by
 * dragging a post onto a slot — and a proposal sends nothing. `scheduled` is
 * the one that goes out on its own, and it only ever comes from somebody
 * confirming a prepared task. Reading the two as the same colour is how a brand
 * would find out on Thursday what it agreed to on Monday.
 */
export function needsConfirming(row: CalendarPost) {
	return row.state === "planned";
}
export function canMoveCalendarPost(row: CalendarPost) {
	return (
		!row.url &&
		["planned", "draft", "scheduled", "ready", "filled"].includes(row.state) &&
		row.task?.schedule_state !== "running"
	);
}
export interface CalendarPost {
	id: string;
	page: string;
	platform: string;
	title: string;
	body: string;
	plannedAt: string | null;
	date: string | null;
	state: string;
	url?: string | null;
	task?: BrowserTask;
	post?: DistributionPost;
}
export function calendarPosts(posts: DistributionPost[], tasks: BrowserTask[]): CalendarPost[] {
	const ordered = [...tasks].sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""));
	const used = new Set<string>();
	const rows: CalendarPost[] = posts.map((post) => {
		const task = ordered.find(
			(t) =>
				t.payload.source.page_id === post.page_id && t.payload.platform === post.platform_id && t.state !== "cancelled",
		);
		if (task) used.add(task.id);
		const completed = task?.state === "published" || task?.state === "user_reported" || !!post.published_url;
		return {
			id: post.id,
			page: post.page_id,
			platform: post.platform_id,
			title: post.title,
			body: task?.payload.fields.body || post.body,
			plannedAt: task?.schedule_state === "waiting" ? (task.scheduled_at ?? post.planned_at) : post.planned_at,
			date: completed
				? (task?.updated_at ?? post.updated_at)
				: task?.schedule_state === "waiting"
					? (task.scheduled_at ?? post.planned_at)
					: post.planned_at,
			state: completed
				? "recorded"
				: task?.schedule_state === "waiting"
					? "scheduled"
					: task && !["ready", "filled"].includes(task.state)
						? task.state
						: post.planned_at
							? "planned"
							: "draft",
			url: task?.published_url || post.published_url,
			task,
			post,
		};
	});
	for (const task of ordered) {
		if (used.has(task.id) || task.state === "cancelled") continue;
		const completed = ["published", "user_reported"].includes(task.state);
		rows.push({
			id: task.id,
			page: task.payload.source.content_id ? "" : task.payload.source.page_id,
			platform: task.payload.platform,
			title: task.payload.fields.title || task.payload.fields.body?.slice(0, 100) || task.payload.platform,
			body: task.payload.fields.body || "",
			plannedAt: task.schedule_state === "waiting" ? (task.scheduled_at ?? null) : null,
			date: completed
				? (task.updated_at ?? null)
				: task.schedule_state === "waiting"
					? (task.scheduled_at ?? null)
					: null,
			state: completed
				? "recorded"
				: task.schedule_state === "waiting"
					? "scheduled"
					: task.state === "ready"
						? "draft"
						: task.state,
			url: task.published_url,
			task,
		});
	}
	return rows;
}
